import { Box, Circle, Text, colors, dimensions } from '@factorialco/deprecated-design-system'
import { F0AvatarAlert } from '@factorialco/f0-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'
import { useTotal } from 'modules/finance/components/FinancialWorkspace/Providers/TotalProvider'
import EmptyState from 'modules/finance/components/FinancialWorkspace/components/EmptyState'
import { ChartData } from 'modules/finance/components/FinancialWorkspace/components/Table/types'
import { centToFormattedAmount } from 'modules/finance/components/FinancialWorkspace/components/chart/helpers'
import { LineChart } from 'modules/finance/components/FinancialWorkspace/components/chart/index'

const COLORS = [colors.viridian1000, colors.purple1000, colors.champagne1200]

const PayrollChart = () => {
  const { formattedState: variables } = useFinancialWorkspace()
  const { plotData: conceptsData, loading, error, chartProps } = useTotal()
  const {
    access: { locale: accessLocale },
    company: { currency },
  } = useCurrentSession()

  const tooManyConcepts =
    chartProps.variables.conceptIds && chartProps.variables.conceptIds.length > 3
  const noConceptsSelected = !loading && !error && !!chartProps.costType && !conceptsData?.length

  const chartData: ChartData[] = useUnsafeMemo(() => {
    const data: ChartData[] = []

    if (conceptsData) {
      conceptsData.forEach(({ label, data: plotData }, index) => {
        data.push({
          serieId: label,
          data: [...plotData],
          pointColor: COLORS[index] || '',
          hoveredPointColor: COLORS[index] || '',
        })
      })
    }

    return data
  }, [conceptsData])

  const supportLink = () => (
    <a
      target="_blank"
      rel="noopener noreferrer"
      href="https://help.factorialhr.com/en_US/managing-my-company-account/how-to-contact-customer-support"
    >
      {i18n.t('finance.financial_workspace.error.contact_support')}
    </a>
  )

  const toAmount = (value: number) => {
    const cents = Number(value) || 0
    return centToFormattedAmount(cents, accessLocale, currency)
  }

  const totals = loading ? null : (
    <Box
      flexWrap="wrap"
      flexDirection="row"
      paddingBottom="s12"
      style={{ columnGap: dimensions.s64, rowGap: dimensions.s20 }}
    >
      {(conceptsData || []).map(({ id, label, data: plotData }, index) => {
        const total = plotData.reduce(
          (accumulator, currentValue) => accumulator + (currentValue.y || 0),
          0
        )

        return (
          <Box flexDirection="column" key={`${id}-${label}`}>
            <Box flexDirection="row" gap="s8" alignItems="center">
              <>
                <Circle color={COLORS[index]} size={10} />
              </>
              <Text.Normal weight="medium" size="200">
                {label}
              </Text.Normal>
            </Box>
            <Box paddingLeft="s16">
              <Text.Normal color="grey1000" weight="bold" size="300">
                {toAmount(total).amount}
              </Text.Normal>
            </Box>
          </Box>
        )
      })}
    </Box>
  )

  const renderOverlay = ({
    type,
    title,
    description,
  }: {
    title: string
    description: string
    type: 'info' | 'critical' | 'warning' | 'positive'
  }) => {
    return (
      <Box
        gap="s12"
        width="full"
        height="s300"
        paddingY="s96"
        alignItems="center"
        justifyContent="center"
        style={{ zIndex: '100' }}
      >
        <F0AvatarAlert type={type} size="lg" />
        <Box justifyContent="center" alignItems="center" gap="s2">
          <Text.Normal weight="medium" color="grey1000" size="400">
            {title}
          </Text.Normal>
          <Text.Normal>{description}</Text.Normal>
        </Box>
      </Box>
    )
  }

  if (error) {
    return (
      <Box height="s300" width="full">
        <EmptyState
          icon="monitor-flash"
          title={i18n.t('finance.financial_workspace.error.generic_title')}
          description={i18n.tView('finance.financial_workspace.error.generic_description', {
            link: supportLink(),
          })}
        />
      </Box>
    )
  }

  if (!chartData.length && !loading && !noConceptsSelected && !chartProps.skip) {
    return null
  }

  return (
    <>
      <Box
        width="full"
        padding="s16"
        borderRadius={{ all: 'abs016' }}
        border={{ all: { color: 'grey400', width: 's1', style: 'solid' } }}
      >
        {tooManyConcepts ? null : totals}
        <Box
          width="full"
          height="s300"
          position={tooManyConcepts || noConceptsSelected ? 'absolute' : undefined}
        >
          <LineChart
            loading={loading}
            toAmount={toAmount}
            period={variables.period}
            chartData={tooManyConcepts ? [] : chartData}
          />
        </Box>
          {tooManyConcepts &&
            renderOverlay({
              type: 'warning',
              title: i18n.t('finance.financial_workspace.too_many_concepts'),
              description: i18n.t('finance.financial_workspace.reduce_concepts'),
            })}
          {noConceptsSelected &&
            renderOverlay({
              type: 'info',
              title: i18n.t('finance.financial_workspace.no_concepts_selected'),
              description: i18n.t('finance.financial_workspace.select_concepts'),
            })}
      </Box>
    </>
  )
}

export { PayrollChart }
